import { FC, useMemo } from "react";
import SearchConditionTile from "@/components/SearchConditionTile";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import { useForm } from "react-hook-form";
import useSearchWithQuery from "@/hooks/useSearchWithQuery";
import {
  foramtFireProtectionMaterial,
  formatFireRegistance,
  formatFlamePreventionRegulation,
  formatFormaldehyde,
} from "@/utils/formatProductDetail";
import FormaldehydeSelection from "@/components/FormaldehydeSelection";
import { useState } from "react";
import FireRegistanceSelection from "@/components/FireRegistanceSelection";
import FlamePreventionSelection from "@/components/FlamePreventionSelection";
import FireProtectionMaterialSelection from "@/components/FireProtectionMaterialSelection";
import ModalSearchChildContainer from "@/components/ModalSearchChildContainer";
import SearchConditionsTile from "@/components/SearchConditionsTile";
import SizeSelection from "@/components/SizeSelection";
import formatRange from "@/utils/formatRange";
import PriceSelection from "@/components/PriceSelection";
import { SizeRangeSliderUnit } from "@/consts/RangeSlider";
import { ProductKind, SearchConditionGroupResponse } from "@/web-client/api";
import {
  useGetSearchConditionGroupsByCategoryId,
  useSelectedSearchConditionGroups,
} from "@/features/search_product";
import { SearchConditionSelection } from "@/features/search_product/components/SearchConditionSelection";

interface Props {
  query: ProductSearchQueryParameter;
  closeHandler: () => void;
  kind?: ProductKind;
}

// 入力フォームの値をすべてここに書いてください
const initValues = {
  keyword: "",
  search_condition_value_ids: "",
  formaldehyde: "",
  fire_protection_material: "",
  flame_prevention_regulation: "",
  fire_registance: "",
  min_width: "",
  max_width: "",
  min_height: "",
  max_height: "",
  min_depth: "",
  max_depth: "",
  min_price: "",
  max_price: "",
};

const ModalSearchProductChild: FC<Props> = ({ query, closeHandler, kind }) => {
  // 空文字を入れておかないと、リセット時にErrorが吐かれる
  const defaultValues = useMemo(() => {
    return {
      ...initValues,
      ...query,
    };
  }, [query]);

  const { register, handleSubmit, reset, setValue, control, watch } =
    useForm<ProductSearchQueryParameter>({
      defaultValues,
    });

  //
  const [selection, setSelection] = useState<{
    id: keyof ProductSearchQueryParameter | "size" | "price";
    search_condition_group?: SearchConditionGroupResponse;
  }>(null);

  const search = useSearchWithQuery<ProductSearchQueryParameter>();

  const onSubmit = (data: ProductSearchQueryParameter) => {
    search(data);
    closeHandler();
  };

  const backHandler = () => setSelection(null);
  const watchingValues = watch();

  const maxWidth = watchingValues.max_width;
  const minWidth = watchingValues.min_width;
  const widthLabel = useMemo(
    () => formatRange(SizeRangeSliderUnit, minWidth, maxWidth),
    [maxWidth, minWidth],
  );

  const maxHeight = watchingValues.max_height;
  const minHeight = watchingValues.min_height;
  const heightLabel = useMemo(
    () => formatRange(SizeRangeSliderUnit, minHeight, maxHeight),
    [maxHeight, minHeight],
  );

  const maxDepth = watchingValues.max_depth;
  const minDepth = watchingValues.min_depth;
  const depthLabel = useMemo(
    () => formatRange(SizeRangeSliderUnit, minDepth, maxDepth),
    [maxDepth, minDepth],
  );

  const maxPrice = watchingValues.max_price;
  const minPrice = watchingValues.min_price;
  const priceLabel = useMemo(
    () => formatRange("円", minPrice, maxPrice),
    [maxPrice, minPrice],
  );

  const { data: searchConditionGroups } =
    useGetSearchConditionGroupsByCategoryId(
      watchingValues.category_id
        ? Number(watchingValues.category_id)
        : undefined,
    );

  const { selectedSearchConditionGroups } = useSelectedSearchConditionGroups(
    watchingValues.search_condition_value_ids
      ? watchingValues.search_condition_value_ids
          .split(",")
          .map((v) => Number(v))
      : [],
    watchingValues.category_id ? Number(watchingValues.category_id) : undefined,
  );

  return (
    <ModalSearchChildContainer<ProductSearchQueryParameter>
      handleSubmit={handleSubmit}
      register={register}
      onSubmit={onSubmit}
      onClear={() => reset(initValues)}
      modalElements={
        <>
          {selection?.id === "search_condition_value_ids" && (
            <SearchConditionSelection
              searchConditionGroup={selection.search_condition_group}
              defaultValue={watchingValues}
              onUpdate={(value) => {
                setValue(
                  "search_condition_value_ids",
                  value.search_condition_value_ids,
                );
              }}
              onBack={backHandler}
            />
          )}

          {selection?.id === "formaldehyde" && (
            <FormaldehydeSelection
              defaultValue={watchingValues}
              onUpdate={({ formaldehyde }) =>
                setValue("formaldehyde", formaldehyde)
              }
              onBack={backHandler}
            />
          )}

          {selection?.id === "flame_prevention_regulation" && (
            <FlamePreventionSelection
              defaultValue={watchingValues}
              onUpdate={({ flame_prevention_regulation }) =>
                setValue(
                  "flame_prevention_regulation",
                  flame_prevention_regulation,
                )
              }
              onBack={backHandler}
            />
          )}

          {selection?.id === "fire_registance" && (
            <FireRegistanceSelection
              defaultValue={watchingValues}
              onUpdate={({ fire_registance }) =>
                setValue("fire_registance", fire_registance)
              }
              onBack={backHandler}
            />
          )}

          {selection?.id === "fire_protection_material" && (
            <FireProtectionMaterialSelection
              defaultValue={watchingValues}
              onUpdate={({ fire_protection_material }) =>
                setValue("fire_protection_material", fire_protection_material)
              }
              onBack={backHandler}
            />
          )}

          {selection?.id === "size" && (
            <SizeSelection
              defaultValue={watchingValues}
              onUpdate={({
                min_width,
                max_width,
                min_height,
                max_height,
                min_depth,
                max_depth,
              }) => {
                setValue("min_width", min_width);
                setValue("max_width", max_width);
                setValue("min_height", min_height);
                setValue("max_height", max_height);
                setValue("min_depth", min_depth);
                setValue("max_depth", max_depth);
              }}
              onBack={backHandler}
            />
          )}

          {selection?.id === "price" && (
            <PriceSelection
              defaultValue={watchingValues}
              onUpdate={({ min_price, max_price }) => {
                setValue("min_price", min_price);
                setValue("max_price", max_price);
              }}
              onBack={backHandler}
            />
          )}
        </>
      }
      hiddenModal={selection?.id == null}
    >
      <ul className="BorderList">
        {defaultValues.category_id && (
          <>
            {searchConditionGroups.map((searchConditonGroup) => (
              <li className="flex" key={searchConditonGroup.id}>
                <SearchConditionsTile
                  label={searchConditonGroup.label}
                  conditionLabel={
                    <div>
                      {selectedSearchConditionGroups
                        .find((group) => searchConditonGroup.id === group.id)
                        ?.values.map((v) => v.label)
                        .join(",")}
                    </div>
                  }
                  onTap={() => {
                    setSelection({
                      id: "search_condition_value_ids",
                      search_condition_group: searchConditonGroup,
                    });
                  }}
                />
              </li>
            ))}
          </>
        )}

        <li className="flex">
          <SearchConditionsTile
            label={"サイズ"}
            conditionLabel={
              <div>
                {widthLabel && <div>幅: {widthLabel}</div>}
                {heightLabel && <div>高さ: {heightLabel}</div>}
                {depthLabel && <div>奥行き: {depthLabel}</div>}
              </div>
            }
            onTap={() => setSelection({ id: "size" })}
          />
        </li>

        <li className="flex">
          <SearchConditionsTile
            label={"価格"}
            conditionLabel={<>{priceLabel && <div>{priceLabel}</div>}</>}
            onTap={() => setSelection({ id: "price" })}
          />
        </li>
        {kind === ProductKind.BUILDING_MATERIAL && (
          <>
            <li className="flex">
              <SearchConditionTile
                control={control}
                name="fire_protection_material"
                label={"防火材料"}
                formatCondition={(value) =>
                  value ? foramtFireProtectionMaterial(Number(value)) : ""
                }
                onTap={() => setSelection({ id: "fire_protection_material" })}
              />
            </li>
            <li className="flex">
              <SearchConditionTile
                control={control}
                name="flame_prevention_regulation"
                label={"防炎規制"}
                formatCondition={(value) =>
                  value ? formatFlamePreventionRegulation(Number(value)) : ""
                }
                onTap={() =>
                  setSelection({ id: "flame_prevention_regulation" })
                }
              />
            </li>
            <li className="flex">
              <SearchConditionTile
                name="fire_registance"
                label={"耐火性能"}
                control={control}
                onTap={() => setSelection({ id: "fire_registance" })}
                formatCondition={(value) =>
                  value ? formatFireRegistance(Number(value)) : ""
                }
              />
            </li>
            <li className="flex">
              <SearchConditionTile
                control={control}
                name="formaldehyde"
                label={"ホルムアルデヒド等級"}
                formatCondition={(value) =>
                  value ? formatFormaldehyde(Number(value)) : ""
                }
                onTap={() => setSelection({ id: "formaldehyde" })}
              />
            </li>
          </>
        )}
      </ul>
    </ModalSearchChildContainer>
  );
};
export default ModalSearchProductChild;
